import { useTranslation } from "@redotech/redo-i18n";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  RETURN_RATE_SOURCE_TYPE,
  type ReturnRateSourceSelection,
} from "@redotech/redo-return-core-common/shipping/return-rate-source-selection";
import { useContext, useMemo, useState } from "react";
import { useReturnRateSourceCarrierAccounts } from "../hooks/use-return-rate-source-carrier-accounts";
import { useReturnRateSourceDropoffIntegrations } from "../hooks/use-return-rate-source-dropoff-integrations";
import { CarrierAccountsCard } from "./components/carrier-accounts-card";
import { DropoffsCard } from "./components/dropoffs-card";
import { RateSourceSelectionSkeleton } from "./components/rate-source-selection-skeleton";
import { RateSourceSelectionWarning } from "./components/rate-source-selection-warning";
import { RateSourceSelectionWrapper } from "./components/rate-source-selection-wrapper";
import {
  getAccountServiceLevelSelection,
  getAvailableReturnRateSourceSelection,
  getCarrierAccountRateSourceOptions,
  getDropoffRateSourceOptions,
  getMerchantCarrierAccountRateSourceOptions,
  getMerchantCarrierAccountsChoice,
  getRedoCarrierAccountsChoice,
  getRedoCarrierRateSourceOptions,
  getRedoCarriersAllSelection,
  getReturnRateSourceSelectionForUpdates,
  getSelectedDropoffRateSourceOptions,
  getSelectedSpecificMerchantCarrierAccountRateSourceOptions,
  getSelectedSpecificRedoCarrierRateSourceOptions,
  getSpecificMerchantCarrierAccountRateSourceOptions,
  updateAccountAllServiceLevels,
  updateAccountServiceLevel,
  updateAllRedoCarrierRateSources,
  updateDropoffRateSource,
  updateMerchantCarrierAccountRateSources,
  updateRedoCarrierAccountRateSources,
  updateSpecificMerchantCarrierAccountRateSource,
  updateSpecificRedoCarrierRateSource,
  type AccountServiceLevelSelection,
  type MerchantCarrierAccountsChoice,
  type RedoCarrierAccountsChoice,
} from "./rate-source-selection-utils";

interface RateSourceSelectionProps {
  rateSourceSelection?: ReturnRateSourceSelection;
  updateRateSourceSelection(
    rateSourceSelection: ReturnRateSourceSelection,
  ): void;
}

export function RateSourceSelection({
  rateSourceSelection,
  updateRateSourceSelection,
}: RateSourceSelectionProps) {
  const { t, i18n } = useTranslation();
  const team = useContext(TeamContext);
  const carrierAccountsQuery = useReturnRateSourceCarrierAccounts();
  const dropoffIntegrationsQuery = useReturnRateSourceDropoffIntegrations();

  const allCarrierAccountOptions = useMemo(() => {
    void i18n.language;
    return getCarrierAccountRateSourceOptions({
      carrierAccountsResult: carrierAccountsQuery.data,
      team,
    });
  }, [carrierAccountsQuery.data, team, i18n.language]);

  const merchantCarrierAccountOptions = useMemo(() => {
    void i18n.language;
    return getMerchantCarrierAccountRateSourceOptions({
      carrierAccountsResult: carrierAccountsQuery.data,
      team,
    });
  }, [carrierAccountsQuery.data, team, i18n.language]);

  const specificMerchantCarrierAccountOptions = useMemo(() => {
    return getSpecificMerchantCarrierAccountRateSourceOptions({
      options: merchantCarrierAccountOptions,
    });
  }, [merchantCarrierAccountOptions]);

  const redoCarrierOptions = useMemo(
    () => getRedoCarrierRateSourceOptions({ team }),
    [team],
  );

  const allDropoffOptions = useMemo(() => {
    return getDropoffRateSourceOptions({
      dropoffIntegrations: dropoffIntegrationsQuery.integrations,
      team,
    });
  }, [dropoffIntegrationsQuery.integrations, team]);

  const currentRateSourceSelection = useMemo(() => {
    return getAvailableReturnRateSourceSelection({
      carrierAccountOptions: allCarrierAccountOptions,
      dropoffOptions: allDropoffOptions,
      rateSourceSelection,
      team,
    });
  }, [allCarrierAccountOptions, allDropoffOptions, rateSourceSelection, team]);
  const rateSourceOptionsLoaded =
    carrierAccountsQuery.isSuccess && dropoffIntegrationsQuery.isSuccess;
  const rateSourceSelectionForUpdates = useMemo(() => {
    return getReturnRateSourceSelectionForUpdates({
      currentRateSourceSelection,
      rateSourceOptionsLoaded,
      rateSourceSelection,
    });
  }, [
    currentRateSourceSelection,
    rateSourceOptionsLoaded,
    rateSourceSelection,
  ]);

  const selectedSpecificMerchantCarrierAccountOptions = useMemo(() => {
    void i18n.language;
    return getSelectedSpecificMerchantCarrierAccountRateSourceOptions({
      options: specificMerchantCarrierAccountOptions,
      rateSourceSelection: currentRateSourceSelection,
    });
  }, [
    currentRateSourceSelection,
    specificMerchantCarrierAccountOptions,
    i18n.language,
  ]);

  const selectedRedoCarrierOptions = useMemo(() => {
    return getSelectedSpecificRedoCarrierRateSourceOptions({
      options: redoCarrierOptions,
      rateSourceSelection: currentRateSourceSelection,
    });
  }, [currentRateSourceSelection, redoCarrierOptions]);

  const selectedRedoCarrierOptionIds = useMemo(
    () => new Set(selectedRedoCarrierOptions.map((option) => option.id)),
    [selectedRedoCarrierOptions],
  );
  const redoCarriersAllSelection = useMemo(
    () =>
      getRedoCarriersAllSelection({
        options: redoCarrierOptions,
        rateSourceSelection: currentRateSourceSelection,
      }),
    [currentRateSourceSelection, redoCarrierOptions],
  );

  const selectedDropoffOptions = useMemo(() => {
    void i18n.language;
    return getSelectedDropoffRateSourceOptions({
      options: allDropoffOptions,
      rateSourceSelection: currentRateSourceSelection,
    });
  }, [allDropoffOptions, currentRateSourceSelection, i18n.language]);

  const selectedSpecificMerchantCarrierAccountOptionIds = useMemo(
    () =>
      new Set(
        selectedSpecificMerchantCarrierAccountOptions.map(
          (option) => option.id,
        ),
      ),
    [selectedSpecificMerchantCarrierAccountOptions],
  );

  const accountServiceLevelSelections = useMemo(() => {
    const selections = new Map<string, AccountServiceLevelSelection>();
    for (const option of specificMerchantCarrierAccountOptions) {
      selections.set(
        option.id,
        getAccountServiceLevelSelection({
          option,
          rateSourceSelection: currentRateSourceSelection,
        }),
      );
    }
    return selections;
  }, [currentRateSourceSelection, specificMerchantCarrierAccountOptions]);

  // "Specific accounts" with nothing checked stores the same selection as
  // "None", so the radio needs local state to stay on Specific while empty.
  const [merchantChoiceOverride, setMerchantChoiceOverride] = useState<
    MerchantCarrierAccountsChoice | undefined
  >();
  const [redoChoiceOverride, setRedoChoiceOverride] = useState<
    RedoCarrierAccountsChoice | undefined
  >();
  const derivedMerchantCarrierAccountsChoice = getMerchantCarrierAccountsChoice(
    currentRateSourceSelection,
  );
  // The override only matters while the stored selection is ambiguous ("none");
  // once data distinguishes the choice, drop it so external resets can't go stale.
  if (
    derivedMerchantCarrierAccountsChoice !== "none" &&
    merchantChoiceOverride !== undefined
  ) {
    setMerchantChoiceOverride(undefined);
  }
  const derivedRedoCarrierAccountsChoice = getRedoCarrierAccountsChoice(
    currentRateSourceSelection,
  );
  if (
    derivedRedoCarrierAccountsChoice !== "none" &&
    redoChoiceOverride !== undefined
  ) {
    setRedoChoiceOverride(undefined);
  }

  const selectedDropoffOptionIds = useMemo(
    () => new Set(selectedDropoffOptions.map((option) => option.id)),
    [selectedDropoffOptions],
  );

  if (carrierAccountsQuery.rateSourceSelectionPending) {
    return (
      <RateSourceSelectionWrapper>
        <RateSourceSelectionSkeleton />
      </RateSourceSelectionWrapper>
    );
  }

  if (!carrierAccountsQuery.rateSourceSelectionEnabled) {
    return null;
  }

  const redoCarrierAccountsAvailable = allCarrierAccountOptions.some(
    (option) => option.value.type === RETURN_RATE_SOURCE_TYPE.REDO_RATES,
  );
  const merchantCarrierAccountsAvailable =
    specificMerchantCarrierAccountOptions.length > 0;
  const merchantCarrierAccountsLoading =
    carrierAccountsQuery.isPending && merchantCarrierAccountOptions.length > 0;
  const redoCarrierAccountsChoice =
    derivedRedoCarrierAccountsChoice === "none"
      ? (redoChoiceOverride ?? derivedRedoCarrierAccountsChoice)
      : derivedRedoCarrierAccountsChoice;
  const merchantCarrierAccountsChoice =
    derivedMerchantCarrierAccountsChoice === "none"
      ? (merchantChoiceOverride ?? derivedMerchantCarrierAccountsChoice)
      : derivedMerchantCarrierAccountsChoice;
  const showCarrierAccountsCard =
    redoCarrierAccountsAvailable ||
    merchantCarrierAccountsAvailable ||
    merchantCarrierAccountsLoading;
  const hasSelectedRedoCarrierAccountRateSource =
    redoCarrierAccountsAvailable &&
    (redoCarrierAccountsChoice === "all" ||
      selectedRedoCarrierOptions.length > 0);
  const hasSelectedAllMerchantCarrierAccountRateSources =
    merchantCarrierAccountsAvailable && merchantCarrierAccountsChoice === "all";
  const hasSelectedSpecificMerchantCarrierAccountRateSource =
    merchantCarrierAccountsAvailable &&
    selectedSpecificMerchantCarrierAccountOptions.length > 0;
  const hasSelectedCarrierAccountRateSource =
    hasSelectedRedoCarrierAccountRateSource ||
    hasSelectedAllMerchantCarrierAccountRateSources ||
    hasSelectedSpecificMerchantCarrierAccountRateSource;
  const hasSelectedRateSource =
    hasSelectedCarrierAccountRateSource || selectedDropoffOptions.length > 0;
  const showNoRateSourcesWarning =
    !carrierAccountsQuery.isPending &&
    !dropoffIntegrationsQuery.isPending &&
    !hasSelectedRateSource;

  if (
    !showCarrierAccountsCard &&
    allDropoffOptions.length === 0 &&
    !showNoRateSourcesWarning
  ) {
    return null;
  }

  return (
    <RateSourceSelectionWrapper>
      {showCarrierAccountsCard && (
        <CarrierAccountsCard
          merchant={
            merchantCarrierAccountsAvailable || merchantCarrierAccountsLoading
              ? {
                  loading: merchantCarrierAccountsLoading,
                  choice: merchantCarrierAccountsChoice,
                  options: specificMerchantCarrierAccountOptions,
                  selectedOptionIds:
                    selectedSpecificMerchantCarrierAccountOptionIds,
                  serviceLevelSelections: accountServiceLevelSelections,
                  onChoiceChange: (choice) => {
                    setMerchantChoiceOverride(choice);
                    updateRateSourceSelection(
                      updateMerchantCarrierAccountRateSources({
                        choice,
                        rateSourceSelection: rateSourceSelectionForUpdates,
                      }),
                    );
                  },
                  onOptionChange: (option, selected) => {
                    setMerchantChoiceOverride("specific");
                    updateRateSourceSelection(
                      updateSpecificMerchantCarrierAccountRateSource({
                        option,
                        selected,
                        rateSourceSelection: rateSourceSelectionForUpdates,
                      }),
                    );
                  },
                  onAllServiceLevelsChange: (option, selected) => {
                    setMerchantChoiceOverride("specific");
                    updateRateSourceSelection(
                      updateAccountAllServiceLevels({
                        option,
                        selected,
                        rateSourceSelection: rateSourceSelectionForUpdates,
                      }),
                    );
                  },
                  onServiceLevelToggle: (option, serviceLevel, selected) => {
                    setMerchantChoiceOverride("specific");
                    updateRateSourceSelection(
                      updateAccountServiceLevel({
                        option,
                        serviceLevel,
                        selected,
                        rateSourceSelection: rateSourceSelectionForUpdates,
                      }),
                    );
                  },
                }
              : undefined
          }
          redo={
            redoCarrierAccountsAvailable
              ? {
                  choice: redoCarrierAccountsChoice,
                  options: redoCarrierOptions,
                  selectedOptionIds: selectedRedoCarrierOptionIds,
                  allSelection: redoCarriersAllSelection,
                  onChoiceChange: (choice) => {
                    setRedoChoiceOverride(choice);
                    updateRateSourceSelection(
                      updateRedoCarrierAccountRateSources({
                        choice,
                        rateSourceSelection: rateSourceSelectionForUpdates,
                      }),
                    );
                  },
                  onOptionChange: (option, selected) => {
                    setRedoChoiceOverride("specific");
                    updateRateSourceSelection(
                      updateSpecificRedoCarrierRateSource({
                        option,
                        selected,
                        rateSourceSelection: rateSourceSelectionForUpdates,
                      }),
                    );
                  },
                  onAllChange: (selected) => {
                    setRedoChoiceOverride("specific");
                    updateRateSourceSelection(
                      updateAllRedoCarrierRateSources({
                        options: redoCarrierOptions,
                        selected,
                        rateSourceSelection: rateSourceSelectionForUpdates,
                      }),
                    );
                  },
                }
              : undefined
          }
        />
      )}
      {allDropoffOptions.length > 0 && (
        <DropoffsCard
          dropoffOptions={allDropoffOptions}
          onDropoffChange={(option, selected) => {
            updateRateSourceSelection(
              updateDropoffRateSource({
                option,
                selected,
                rateSourceSelection: rateSourceSelectionForUpdates,
              }),
            );
          }}
          selectedDropoffOptionIds={selectedDropoffOptionIds}
        />
      )}
      {showNoRateSourcesWarning && (
        <RateSourceSelectionWarning
          description={t(
            "This location flow will not be able to get any rates until at least one carrier account or dropoff is selected.",
          )}
          title={t("No rate sources selected")}
        />
      )}
    </RateSourceSelectionWrapper>
  );
}
